/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoDatasource } from '@cloudio-saas/datasource-types';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { format } from 'date-fns';
import { InitIoDatasource } from 'layout/Sidebar/panels/DataSourceView/DataSourcePanel';
import React, { useState } from 'react';
import { useUserDateFormat } from 'root/useUser';
import { isFromDB } from 'store/storeTypes';
import useDataStore from 'store/useDataStore';
import { getIoDatasourceStoreProps } from 'store/utils/utils';
import { SearchField, WizardOption } from '../../common';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  body: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowX: 'hidden',
    overflowY: 'auto',
    gap: '16px',
  },
}));

const PageWizardChooseDatasource = ({
  onSelect,
  appUid,
  width,
}: {
  width: number;
  appUid: string;
  onSelect: (ds: string) => void;
}): React.ReactElement | null => {
  const classes = useStyles();
  const store = useDataStore<IoDatasource>(getIoDatasourceStoreProps(appUid));
  const rows = useStoreRecords(store);
  const [filter, setFilter] = useState('');
  const allrows = rows.filter(
    (row) => row.appUid === appUid && isFromDB(row),
  ) as DBRow<IoDatasource>[];
  const filteredRows = allrows.filter((row) =>
    row.datasourceUid.toLowerCase().includes(filter),
  );
  const dateFormat = useUserDateFormat();
  return (
    <div className={classes.root}>
      <InitIoDatasource appUid={appUid} />
      <SearchField
        data-testid="pw-ds-filter"
        onChange={(f) => setFilter(f)}
        tip={`${
          filteredRows.length === allrows.length
            ? ''
            : `${filteredRows.length} / `
        }${allrows.length} data sources`}
      />
      <div className={classes.body}>
        {filteredRows.map((row) => (
          <WizardOption
            data-testid={row.datasourceUid}
            key={row.datasourceUid}
            iconName="database"
            title={row.datasourceUid}
            description={`Last updated: ${format(
              new Date(row.lastUpdateDate),
              dateFormat,
            )} by ${row.lastUpdatedBy}`}
            onClick={async () => {
              onSelect(row.datasourceUid);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default PageWizardChooseDatasource;
